import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { X, Lock, Sparkles, BookOpen, CheckCircle } from 'lucide-react';
import paymentService from '../services/paymentService';
import analytics from '../services/analytics';
import logger from '../utils/logger';
import { useLanguage } from '../contexts/LanguageContext';
import { getDisplayPrice, getCurrencySymbol } from '../utils/currencyConfig';

// Check if Stripe publishable key is available
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const PaymentForm = ({ clientSecret, spreadData, onSuccess, onError, onClose, appliedCoupon, language, t }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    
    // Track payment start
    analytics.trackPaymentStart(appliedCoupon?.finalAmount || 249, appliedCoupon);

    try {
      const result = await paymentService.processPayment(clientSecret, spreadData, stripe, elements, language);
      onSuccess(result.explanation);
    } catch (error) {
      logger.error('Payment error:', error);
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{
        background: '#f8f9fa',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>

      <div style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'flex-end'
      }}>
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          style={{
            padding: '0.75rem 1.5rem',
            border: '1px solid #ccc',
            background: 'white',
            color: '#666',
            borderRadius: '25px',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.6 : 1
          }}
        >
          {t('payment.cancel')}
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: isProcessing ? '#ccc' : 'linear-gradient(45deg, #ffd700, #ffed4e)',
            color: '#333',
            borderRadius: '25px',
            cursor: (!stripe || isProcessing) ? 'not-allowed' : 'pointer',
            opacity: (!stripe || isProcessing) ? 0.6 : 1,
            fontWeight: '500'
          }}
        >
          {isProcessing ? t('payment.processing') : `${t('payment.pay')} ${appliedCoupon?.finalAmount ? `${getCurrencySymbol(language)}${(appliedCoupon.finalAmount / 100).toFixed(2)}` : getDisplayPrice(language)}`}
        </button>
      </div>
    </form>
  );
};

const PaymentModal = ({ isOpen, onClose, spreadData, onSuccess }) => {
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [readingId, setReadingId] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [freeAccess, setFreeAccess] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [isGeneratingExplanation, setIsGeneratingExplanation] = useState(false);
  const [paymentStartTime, setPaymentStartTime] = useState(null);
  const [aiStartTime, setAIStartTime] = useState(null);

  useEffect(() => {
    if (isOpen && !paymentSuccess && !freeAccess) {
      // Track payment modal open
      setPaymentStartTime(Date.now());
      analytics.trackPaymentModalOpen(spreadData?.spreadType || 'unknown', 'ai_explanation_request');
      analytics.trackConversionFunnel('payment_modal_opened', spreadData?.spreadType || 'unknown', 'user');
      initializePayment();
    }
  }, [isOpen, paymentSuccess, freeAccess]);

  const initializePayment = async () => {
    setLoading(true);
    setError(null);
    
    if (!stripePublishableKey) {
      setError('Payment service is not configured. Please contact support.');
 setLoading(false);
      return;
    }
    
    try {
      // Track payment initialization
      analytics.trackPaymentInitialization(false, null);
      
      // For initial load, don't pass coupon code - let auto-apply handle first trial
      const result = await paymentService.createPaymentIntent(null, currentLanguage);
      
      if (result.freeAccess) {
        // Handle free access scenario
        setFreeAccess(true);
        setAppliedCoupon(result.appliedCoupon || null);
        setPaymentSuccess(true);
        setIsGeneratingExplanation(true);
        
        // Track free access granted
        analytics.trackFreeAccessGranted(result.appliedCoupon?.code, 'auto_applied_first_time');
        analytics.trackConversionFunnel('payment_completed', spreadData?.spreadType || 'unknown', 'free_user');
        
        try {
          setAIStartTime(Date.now());
          analytics.trackAIExplanationStart(spreadData?.spreadType || 'unknown', 'first_time_free');
          
          // Automatically generate AI explanation for free access
          const explanation = await paymentService.generateAIExplanation(spreadData, currentLanguage);
          
          const generationTime = Date.now() - (aiStartTime || Date.now());
          analytics.trackAIExplanationComplete(spreadData?.spreadType || 'unknown', 'first_time_free', generationTime);
          
          const successResult = await onSuccess(explanation);
          if (successResult && successResult.readingId) {
            setReadingId(successResult.readingId);
          }
        } catch (aiError) {
          analytics.trackAIExplanationError(spreadData?.spreadType || 'unknown', 'first_time_free', aiError.message);
          throw aiError;
        } finally {
          setIsGeneratingExplanation(false);
        }
      } else {
        setClientSecret(result.clientSecret);
        setAppliedCoupon(result.appliedCoupon || null);
      }
    } catch (error) {
      logger.error('Payment initialization error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    logger.payment('🎫 Applying coupon:', couponCode.trim());
    logger.payment('🔑 Auth token available:', !!localStorage.getItem('authToken'));

    setLoading(true);
    setError(null);

    try {
      // Track coupon application attempt
      analytics.trackPaymentInitialization(true, couponCode.trim());
      
      const result = await paymentService.createPaymentIntent(couponCode.trim(), currentLanguage);
      logger.payment('🎫 Coupon application result:', result);
      
      if (result.freeAccess) {
        logger.payment('🆓 Free access granted!');
        // Handle free access scenario
        setFreeAccess(true);
        setAppliedCoupon(result.appliedCoupon || null);
        setPaymentSuccess(true);
        setIsGeneratingExplanation(true);
        
        // Track successful coupon application for free access
        analytics.trackCouponApplied(
          couponCode.trim(),
          result.appliedCoupon?.discountAmount || 249,
          result.appliedCoupon?.discountType || 'percentage',
          result.appliedCoupon?.finalAmount || 0
        );
        analytics.trackFreeAccessGranted(couponCode.trim(), 'manual_coupon');
        analytics.trackConversionFunnel('payment_completed', spreadData?.spreadType || 'unknown', 'coupon_user');
        
        try {
          setAIStartTime(Date.now());
          analytics.trackAIExplanationStart(spreadData?.spreadType || 'unknown', 'free_coupon');
          
          // Automatically generate AI explanation for free access
          const explanation = await paymentService.generateAIExplanation(spreadData, currentLanguage);
          
          const generationTime = Date.now() - (aiStartTime || Date.now());
          analytics.trackAIExplanationComplete(spreadData?.spreadType || 'unknown', 'free_coupon', generationTime);
          
          const successResult = await onSuccess(explanation);
          if (successResult && successResult.readingId) {
            setReadingId(successResult.readingId);
          }
        } catch (aiError) {
          analytics.trackAIExplanationError(spreadData?.spreadType || 'unknown', 'free_coupon', aiError.message);
          throw aiError;
        } finally {
          setIsGeneratingExplanation(false);
        }
      } else {
        logger.payment('💳 Payment required:', { finalAmount: result.appliedCoupon?.finalAmount });
        
        // Track successful coupon application with discount
        if (result.appliedCoupon) {
          analytics.trackCouponApplied(
            couponCode.trim(),
            result.appliedCoupon.discountAmount,
            result.appliedCoupon.discountType,
            result.appliedCoupon.finalAmount
          );
        }
        
        setClientSecret(result.clientSecret);
        setAppliedCoupon(result.appliedCoupon || null);
        setShowCouponField(false);
        setError(null);
      }
    } catch (error) {
      logger.error('❌ Coupon validation error:', error);
      analytics.trackCouponError(couponCode.trim(), error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = async (explanation) => {
    setPaymentSuccess(true);
    setIsGeneratingExplanation(true);
    
    // Track payment success
    const amount = appliedCoupon?.finalAmount || 249; // Default amount in cents
    analytics.trackPaymentSuccess(249, appliedCoupon, `payment_${Date.now()}`);
    analytics.trackConversionFunnel('payment_completed', spreadData?.spreadType || 'unknown', 'paid_user');
    
    try {
      setAIStartTime(Date.now());
      analytics.trackAIExplanationStart(spreadData?.spreadType || 'unknown', 'paid');
      
      // Call the parent's onSuccess to save the reading and get reading ID
      const result = await onSuccess(explanation);
      if (result && result.readingId) {
        setReadingId(result.readingId);
        
        const generationTime = Date.now() - (aiStartTime || Date.now());
        analytics.trackAIExplanationComplete(spreadData?.spreadType || 'unknown', 'paid', generationTime);
      }
    } catch (error) {
      analytics.trackAIExplanationError(spreadData?.spreadType || 'unknown', 'paid', error.message);
      throw error;
    } finally {
      setIsGeneratingExplanation(false);
    }
  };

  const handleError = (error) => {
    logger.error('Payment error:', error);
    analytics.trackPaymentError(error.message, 'processing', appliedCoupon?.finalAmount || 249);
    setError(error.message);
  };

  const resetModal = () => {
    setPaymentSuccess(false);
    setReadingId(null);
    setError(null);
    setClientSecret(null);
    setLoading(false);
    setIsGeneratingExplanation(false);
  };

  const handleClose = () => {
    const stage = paymentSuccess ? 'after_success' : (clientSecret ? 'after_payment' : 'before_payment');
    analytics.trackPaymentModalClose(stage);
    
    // Track total time in payment modal for analytics
    if (paymentStartTime) {
      const totalTime = Date.now() - paymentStartTime;
      analytics.trackEvent('payment_modal_duration', {
        event_category: 'User Behavior',
        duration_ms: totalTime,
        stage: stage
      });
    }
    
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          <X size={24} />
        </button>

        {!paymentSuccess && (
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '60px',
              height: '60px',
              background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
              borderRadius: '50%',
              marginBottom: '1rem'
            }}>
              <Sparkles size={28} color="#333" />
            </div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
              {t('payment.unlockWisdom')}
            </h2>
            <p style={{ color: '#666', margin: 0 }}>
              {t('payment.personalizedInsights')}
            </p>
          </div>
        )}

        {!paymentSuccess && (
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#333' }}>
              {t('payment.whatYouGet')}
            </h3>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#666' }}>
              <li>{t('payment.tarocaInterpretation')}</li>
              <li>{t('payment.personalizedGuidance')}</li>
              <li>{t('payment.actionableInsights')}</li>
              <li>{t('payment.savedToDiary')}</li>
            </ul>
          </div>
        )}

        {/* Applied Coupon Display */}
        {!paymentSuccess && appliedCoupon && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#155724'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <CheckCircle size={16} />
              <strong>{t('payment.couponApplied')} {appliedCoupon.code}</strong>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
              {appliedCoupon.description}
            </p>
            <div style={{ fontSize: '0.9rem' }}>
              <span style={{ textDecoration: 'line-through', color: '#6c757d' }}>
                ${(249/100).toFixed(2)}
              </span>
              {' → '}
              <strong style={{ color: '#155724' }}>
                {getCurrencySymbol(currentLanguage)}{(appliedCoupon.finalAmount ? (appliedCoupon.finalAmount/100).toFixed(2) : '0.00')}
              </strong>
              <span style={{ color: '#155724', marginLeft: '0.5rem' }}>
                ({t('payment.save')} {getCurrencySymbol(currentLanguage)}{(appliedCoupon.discountAmount/100).toFixed(2)})
              </span>
            </div>
          </div>
        )}

        {/* Coupon Input Field */}
        {!paymentSuccess && !appliedCoupon && !freeAccess && (
          <div style={{ marginBottom: '1.5rem' }}>
            {!showCouponField ? (
              <button
                onClick={() => {
                  setShowCouponField(true);
                  analytics.trackCouponFieldOpen();
                }}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  color: '#666',
                  padding: '0.5rem 1rem',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                {t('payment.haveCoupon')}
              </button>
            ) : (
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem',
                background: '#f8f9fa'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    {t('payment.couponCode')}
                  </label>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder={t('payment.enterCoupon')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleApplyCoupon}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      background: loading ? '#ccc' : '#007bff',
                      color: 'white',
                      borderRadius: '25px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {loading ? t('payment.applying') : t('payment.applyCoupon')}
                  </button>
                  <button
                    onClick={() => {
                      setShowCouponField(false);
                      setCouponCode('');
                      setError(null);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #ddd',
                      background: 'white',
                      color: '#666',
                      borderRadius: '25px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {t('payment.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!paymentSuccess && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: '#666'
          }}>
            <Lock size={16} />
            <span>{t('payment.securePayment')}</span>
          </div>
        )}

        {!paymentSuccess && loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{t('payment.initializing')}</p>
          </div>
        )}

        {!paymentSuccess && error && (
          <div style={{
            color: '#dc3545',
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {paymentSuccess ? (
          /* Success View */
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              color: '#28a745', 
              fontSize: '3rem', 
              marginBottom: '1rem' 
            }}>
              <CheckCircle size={64} />
            </div>
            
            <h2 style={{ 
              color: '#28a745', 
              margin: '0 0 1rem 0' 
            }}>
               {t('payment.thankYou')}
            </h2>
            
            <div style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <p style={{ margin: '0 0 1rem 0', color: '#155724' }}>
                <strong>{t('payment.paymentSuccessful')}</strong>
              </p>
              <ul style={{ color: '#155724', margin: '0 0 1rem 0', paddingLeft: '1.5rem' }}>
                <li>{t('payment.readingSaved')}</li>
                <li>{t('payment.accessExplanations')}</li>
              </ul>
              <p style={{ margin: '0', color: '#155724' }}>
                <strong>{t('payment.nextSteps')}</strong><br />
                {t('payment.waitForExplanation')}
              </p>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
                <button
                onClick={() => {
                  onClose();
                  navigate('/diary');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #6c757d',
                  background: 'white',
                  color: '#6c757d',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <BookOpen size={16} />
                {t('payment.viewAllReadings')}
              </button>              {(readingId || isGeneratingExplanation) && (
                <button
                  onClick={() => {
                    if (!isGeneratingExplanation && readingId) {
                      handleClose();
                      navigate(`/reading/${readingId}`);
                    }
                  }}
                  disabled={isGeneratingExplanation}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: isGeneratingExplanation ? '#ccc' : 'linear-gradient(135deg, #d4af37, #b8860b)',
                    color: 'white',
                    borderRadius: '25px',
                    cursor: isGeneratingExplanation ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: '600',
                    opacity: isGeneratingExplanation ? 0.7 : 1
                  }}
                >
                  <Sparkles size={16} />
                  {isGeneratingExplanation ? t('payment.tarocaRevealing') : t('payment.viewThisReading')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {clientSecret && !loading && (
              <Elements stripe={stripePromise}>
                <PaymentForm
                  clientSecret={clientSecret}
                  spreadData={spreadData}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  onClose={onClose}
                  appliedCoupon={appliedCoupon}
                  language={currentLanguage}
                  t={t}
                />
              </Elements>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
